'use client';

import BugReportInfo from '@/components/bug-report-info';
import { ExternalUserForbiddenOverlay } from '@/components/external-user/external-user-forbidden-overlay';
import ImportantMessages from '@/components/important-messages';
import IntercomWrapper from '@/components/intercom/intercom-wrapper';
import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { WindowKeeperTabs } from '@/components/window-keeper/window-keeper-tabs';
import { DatadogInit } from '@/datadog';
import { env } from '@/env';
import { GlobalBeforeUnloadListener } from '@/lib/global-before-unload';
import { PresetProvider } from '@/lib/presets/preset-provider';
import { BrandingProvider } from '@/providers/branding-provider';
import { LocaleProvider } from '@/providers/locale-provider';
import { ModalProvider } from '@/providers/modal-provider';
import { SessionProvider } from '@/providers/session-provider';
import { AuthGuard } from '@/providers/session-provider/auth-guard';
import { ThemeProvider } from '@/providers/theme-provider';
import { WebsocketProvider } from '@/providers/websocket-provider';
import { TRACEABLE_CLIENT_TYPES } from '@/requests/constants';
import { NuqsAdapter } from 'nuqs/adapters/next/app';
import { PromptDialog } from '../components/prompt-dialog';
import '../lib/i18n';
import QueryClientProvider from '../providers/queryClient';
import WindowKeeperProvider from '../providers/windowkeeperProvider';
import HubSpotChatLoader from './hubspot-chat';
import PigelloChatInit from './pigello-chat';
// A single "use client" boundary for the whole provider tree. Keeping this as
// one client reference (instead of ~23 imported directly by the server root
// layout) collapses what Next lists in every route's client-reference-manifest.
export function Providers({ children }: { children: React.ReactNode }) {
  const shouldInitDataDog = TRACEABLE_CLIENT_TYPES.includes(
    env.NEXT_PUBLIC_CLIENT_TYPE
  );
  return (
    <QueryClientProvider>
      <ThemeProvider
        attribute='class'
        defaultTheme='light'
        enableSystem={true}
        disableTransitionOnChange={true}
      >
        <SessionProvider>
          <NuqsAdapter>
            <BrandingProvider>
              <TooltipProvider delayDuration={300}>
                <LocaleProvider>
                  <AuthGuard
                    anonymousRoutes={children}
                    protectedRoutes={
                      <PresetProvider>
                        <IntercomWrapper>
                          <WindowKeeperProvider>
                            <ModalProvider>
                              {children}
                              <WindowKeeperTabs />
                              <PromptDialog />
                              <GlobalBeforeUnloadListener />
                              <PigelloChatInit />
                              <HubSpotChatLoader />
                              {shouldInitDataDog && <DatadogInit />}
                              <BugReportInfo />
                              <WebsocketProvider />
                              <ImportantMessages />
                              <ExternalUserForbiddenOverlay />
                            </ModalProvider>
                          </WindowKeeperProvider>
                        </IntercomWrapper>
                      </PresetProvider>
                    }
                  />
                </LocaleProvider>
              </TooltipProvider>
            </BrandingProvider>
          </NuqsAdapter>
        </SessionProvider>
        <Toaster closeButton richColors position='bottom-center' expand />
      </ThemeProvider>
    </QueryClientProvider>
  );
}
